import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";

const CONSENT_KEY = "cookie-consent-v2";
export const OPEN_CONSENT_EVENT = "open-cookie-consent";
export const OPEN_DNS_EVENT = "open-do-not-sell";

type ConsentValue = "granted" | "denied";

interface ConsentState {
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
  analytics_storage: ConsentValue;
  functionality_storage: ConsentValue;
  security_storage: ConsentValue;
}

const DENY_ALL: ConsentState = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  functionality_storage: "granted",
  security_storage: "granted",
};

const ACCEPT_ALL: ConsentState = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
  functionality_storage: "granted",
  security_storage: "granted",
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    __isEEA?: boolean;
  }
}

function pushConsent(consent: ConsentState) {
  try {
    window.gtag?.("consent", "update", consent);
    // Extra signal for AdSense restricted data processing in CCPA states.
    if (consent.ad_user_data === "denied" || consent.ad_personalization === "denied") {
      window.gtag?.("set", "ads_data_redaction", true);
    }
  } catch {
    /* no-op */
  }
}

function persist(consent: ConsentState) {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ consent, savedAt: new Date().toISOString() }),
    );
  } catch {
    /* no-op */
  }
}

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(true);
  const [personalization, setPersonalization] = useState(true);

  const isEEA = typeof window !== "undefined" && window.__isEEA === true;

  const open = useCallback((mode: "banner" | "customize" | "dns") => {
    // Pre-fill from stored state when reopening.
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { consent?: ConsentState };
        if (parsed.consent) {
          setAnalytics(parsed.consent.analytics_storage === "granted");
          setAds(parsed.consent.ad_storage === "granted");
          setPersonalization(parsed.consent.ad_personalization === "granted");
        }
      }
    } catch {
      /* no-op */
    }
    setShowCustomize(mode === "customize" || mode === "dns");
    if (mode === "dns") {
      // Pre-toggle ads/personalization off so user can save the opt-out.
      setAds(false);
      setPersonalization(false);
    }
    setVisible(true);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const onOpen = () => open("customize");
    const onDns = () => open("dns");
    window.addEventListener(OPEN_CONSENT_EVENT, onOpen);
    window.addEventListener(OPEN_DNS_EVENT, onDns);
    return () => {
      window.removeEventListener(OPEN_CONSENT_EVENT, onOpen);
      window.removeEventListener(OPEN_DNS_EVENT, onDns);
    };
  }, [open]);

  const apply = (consent: ConsentState) => {
    persist(consent);
    pushConsent(consent);
    setVisible(false);
    setShowCustomize(false);
  };

  if (!visible) return null;

  const handleSave = () => {
    apply({
      ...DENY_ALL,
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: ads ? "granted" : "denied",
      ad_user_data: ads ? "granted" : "denied",
      ad_personalization: ads && personalization ? "granted" : "denied",
    });
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie and privacy preferences"
      className="fixed bottom-0 inset-x-0 z-50 animate-[slideUp_0.4s_ease-out] border-t border-border bg-background/95 backdrop-blur-md px-4 py-4 sm:px-6 sm:py-5"
    >
      <div className="mx-auto max-w-5xl">
        {!showCustomize ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <p className="font-body text-sm text-muted-foreground">
              We use cookies for analytics and to serve {isEEA ? "non-personalized" : "personalized"} ads.{" "}
              {isEEA
                ? "Nothing non-essential loads until you choose."
                : "You can opt out of personalized ads anytime."}{" "}
              <Link to="/privacy" className="underline text-primary hover:text-primary/80 transition-colors">
                Privacy Policy
              </Link>
              {" · "}
              <Link to="/terms" className="underline text-primary hover:text-primary/80 transition-colors">
                Terms
              </Link>
            </p>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => setShowCustomize(true)}>
                Customize
              </Button>
              <Button variant="outline" size="sm" onClick={() => apply(DENY_ALL)}>
                Reject all
              </Button>
              <Button size="sm" onClick={() => apply(ACCEPT_ALL)}>
                Accept all
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h2 className="font-display text-lg font-semibold text-foreground">Cookie & privacy preferences</h2>
              <p className="font-body mt-1 text-sm text-muted-foreground">
                Essential cookies are always on. Toggle the rest to your preference. See our{" "}
                <Link to="/privacy" className="underline text-primary hover:text-primary/80 transition-colors">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
                <div>
                  <p className="font-body text-sm font-semibold text-foreground">Essential</p>
                  <p className="font-body text-xs text-muted-foreground">Required for the site to function.</p>
                </div>
                <Switch checked disabled aria-label="Essential cookies (always on)" />
              </div>

              <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
                <div>
                  <p className="font-body text-sm font-semibold text-foreground">Analytics</p>
                  <p className="font-body text-xs text-muted-foreground">Google Analytics — anonymized traffic stats.</p>
                </div>
                <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Analytics cookies" />
              </div>

              <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
                <div>
                  <p className="font-body text-sm font-semibold text-foreground">Advertising</p>
                  <p className="font-body text-xs text-muted-foreground">Google AdSense — show ads on the site.</p>
                </div>
                <Switch
                  checked={ads}
                  onCheckedChange={(v) => {
                    setAds(v);
                    if (!v) setPersonalization(false);
                  }}
                  aria-label="Advertising cookies"
                />
              </div>

              <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
                <div>
                  <p className="font-body text-sm font-semibold text-foreground">Ad personalization</p>
                  <p className="font-body text-xs text-muted-foreground">
                    Use your browsing data to tailor ads. Turning this off triggers our CCPA "Do Not Sell or Share" signal.
                  </p>
                </div>
                <Switch
                  checked={personalization}
                  onCheckedChange={setPersonalization}
                  disabled={!ads}
                  aria-label="Ad personalization"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => apply(DENY_ALL)}>
                Reject all
              </Button>
              <Button variant="outline" size="sm" onClick={() => apply(ACCEPT_ALL)}>
                Accept all
              </Button>
              <Button size="sm" onClick={handleSave}>
                Save preferences
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
